import { useEffect } from 'react';

import {
  BLANK_SAVED_PROMPT_ID,
  createBlankSavedPrompt,
  doesToolSupportPromptInstruction,
  GENERATION_TOOL,
  getIdFromGenerationToolListId,
  getToolFromGenerationToolListId,
  isFreestyleGenerationTool,
  isUsedTemplate,
} from 'src/data/generationTool';
import { useFreestyleToolsActions } from 'src/store/freestyleTools';
import { useProjectActions } from 'src/store/project';

import { INFINITY_EXTENSION_MODE } from '../const';
import { useLocalActions } from '../store';

export const useInitStoreData = ({
  toolListId,
  generating,
  disabledGeneration,
  customMultiPromptId,
  displayInfinityParams,
  advancedOptionsVisible,
}) => {
  const projectActions = useProjectActions();
  const freestyleToolsActions = useFreestyleToolsActions();
  const localActions = useLocalActions();

  useEffect(() => {
    if (!toolListId) {
      return;
    }

    // set cache for previous generation tool before initializing new one
    if (localActions.getGenerationTool()) {
      localActions.updateGenerationToolOptionsCache();
    }

    if (customMultiPromptId) {
      localActions.setCustomMultiPromptId(customMultiPromptId);
    }

    const generationTool = getToolFromGenerationToolListId(toolListId);
    if (generationTool) {
      if (!isUsedTemplate(generationTool) && !displayInfinityParams) {
        localActions.initGenerationTool(GENERATION_TOOL.DEFAULT);
      } else {
        localActions.initGenerationTool(generationTool);
      }
    }

    if (
      isFreestyleGenerationTool(generationTool) ||
      doesToolSupportPromptInstruction(generationTool) ||
      displayInfinityParams /* we should always load "saved prompt" in Infinity "prompt" mode, cause all tools there support it */
    ) {
      // See if we have freestyle tool data available to pre-populate the generation panel
      const freestyleTool = (() => {
        const newProjectPromptData = projectActions.getNewProjectPromptData();
        if (newProjectPromptData) {
          projectActions.clearNewProjectPromptData();
          return {
            allInstructions: newProjectPromptData.text,
            allInstructionsData: newProjectPromptData,
          };
        }

        const freestyleToolId = getIdFromGenerationToolListId(toolListId);
        if (!freestyleToolId) {
          return null;
        }

        if (freestyleToolId === BLANK_SAVED_PROMPT_ID) {
          return createBlankSavedPrompt(generationTool);
        }

        return freestyleToolsActions.getFreestyleTool(freestyleToolId);
      })();

      // Init "local" store with the loaded freestyle tool
      localActions.initFreestyleToolData(freestyleTool);
    }
  }, [
    projectActions,
    freestyleToolsActions,
    localActions,
    toolListId,
    displayInfinityParams,
    customMultiPromptId,
  ]);

  useEffect(() => {
    localActions.setGenerating(generating);
  }, [localActions, generating]);

  useEffect(() => {
    localActions.setDisabledGeneration(disabledGeneration);
  }, [localActions, disabledGeneration]);

  useEffect(() => {
    localActions.setInfinityExtensionMode(
      displayInfinityParams
        ? INFINITY_EXTENSION_MODE.promptGeneration
        : INFINITY_EXTENSION_MODE.variationsGeneration
    );
  }, [localActions, displayInfinityParams]);

  useEffect(() => {
    localActions.setShowAdvancedOptions(advancedOptionsVisible);
  }, [localActions, advancedOptionsVisible]);
};
